/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the activity library as list and kanban views.
 * @state-owner navigation store owns filters and display options; dataset store owns activities
 * @allowed-imports React; API-client error surface; workspace and dataset stores; shared
 *   components, selectors and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation, context menus and dialogs; clears the connected Activities dataset
 *   projection on authoritative 403
 * @tests ActivityBoard.test.tsx; app/router/routes.test.tsx; ACT-F01 browser and Staging Playwright
 *   coverage
 */

import { VispoApiError } from '@vispo/api-client';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { CommandMenuRows, MenuRows } from '../../../shared/components/ContextMenu';
import { FilterBar, useDisplayOptions } from '../../../shared/components/FilterBar';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { Page, PageBody } from '../../../shared/components/PageShell';
import { NameCell, StatusIcon, StatusText } from '../../../shared/components/StatusIcon';
import { GearIcon } from '../../../shared/components/VispoIcon';
import { tableCells, VTable } from '../../../shared/components/VTable';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { filterDefinitions } from '../../../shared/lib/filters';
import { clickable } from '../../../shared/lib/interactive';
import type { ActivityRecord } from '../../../shared/lib/model';
import { myClassIndexes } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useActivityDirectory } from '../api/activities.queries';
import type { ActivityDirectoryStatus, ActivitySummary } from '../model/types';
import { useActivityActions } from '../model/use-activity-actions';
import { useActivityFilter } from '../model/use-activity-filter';
import { AssignmentDialog } from './AssignmentDialog';
import { TagDialog } from './TagDialog';
import './activity-directory.css';

const BOARD_COLUMNS: readonly (readonly [ActivityRecord['status'], string])[] = [
  ['parsing', '解析中'],
  ['review', '待確認'],
  ['ready', '可用'],
];

export function ActivityBoard({ surface }: { surface?: 'desktop' | 'mobile' }) {
  if (surface) return <ActivityDirectory surface={surface} />;
  return <LegacyActivityBoard />;
}

function LegacyActivityBoard() {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useActivityFilter();
  const display = useDisplayOptions('act');
  const pick = useWorkspaceUiStore((state) => state.pick);
  const definitions = filterDefinitions('act', classes, myClassIndexes(classes));
  const { openCardMenu } = useActivityMenus();

  const sorted = [...activities].sort((a, b) => {
    if (display.ord === 'name') return a.name.localeCompare(b.name);
    if (display.ord === 'new') return b.createdAt - a.createdAt;
    if (display.ord === 'upd') return b.updatedAt - a.updatedAt;
    if (display.ord === 'tag') {
      /** Untagged material sorts last rather than first, where it would bury the rest. */
      const left = a.tags[0] ?? '\uffff';
      const right = b.tags[0] ?? '\uffff';
      return left.localeCompare(right) || a.name.localeCompare(b.name);
    }
    if (display.ord === 'st') {
      const order = ['parsing', 'review', 'ready'];
      return order.indexOf(a.status) - order.indexOf(b.status);
    }
    return 0;
  });

  /** Archived material leaves the working columns but keeps its own, so nothing is ever lost. */
  const working = sorted.filter((entry) => !entry.archived);
  const archived = sorted.filter((entry) => entry.archived);

  const columns =
    display.grp === 'ci'
      ? [
          ...classes.map((item, index) => ({
            title: item.name,
            icon: null as React.ReactNode,
            rows: working.filter((entry) => entry.usedByClassIndexes.includes(index)),
          })),
          {
            title: t('未加入班級'),
            icon: null as React.ReactNode,
            rows: working.filter((entry) => entry.usedByClassIndexes.length === 0),
          },
          { title: t('已封存'), icon: null as React.ReactNode, rows: archived },
        ]
      : [
          ...BOARD_COLUMNS.map(([status, label]) => ({
            title: t(label),
            icon: (
              <StatusIcon
                glyph={status === 'ready' ? 'done' : status === 'review' ? 'warn' : 'half'}
              />
            ),
            rows: working.filter((entry) => entry.status === status),
          })),
          {
            title: t('已封存'),
            icon: <StatusIcon glyph="todo" />,
            rows: archived,
          },
        ];

  return (
    <Page>
      <div className="vtbar">
        <FilterBar definitions={definitions} scope="act" />
      </div>
      {display.view === 'list' ? (
        <PageBody>
          <VTable
            empty={t('沒有符合的')}
            columns={[
              { heading: t('教材') },
              { heading: t('題數') },
              { heading: t('班級') },
              { heading: t('答案來源') },
              { heading: t('狀態') },
            ]}
            rows={sorted.map((activity) => ({
              key: activity.id,
              onSelect: () => pick(activity.id),
              onContextMenu: (event) => openCardMenu(event, activity),
              cells: tableCells(
                <NameCell icon={t('題字')} text={activity.name} />,
                <span className="vmut">{`${activity.questionCount} ${t('題')}`}</span>,
                activity.usedByClassIndexes.length ? (
                  <span className="vchip">{classes[activity.usedByClassIndexes[0]].name}</span>
                ) : (
                  <span className="vmut">{t('未加入班級')}</span>
                ),
                activity.answerSource === 'ai' ? (
                  <span className="vcai">{t('AI 生成')}</span>
                ) : (
                  <span className="vmut">{t('有答案檔')}</span>
                ),
                activity.status === 'ready' ? (
                  <StatusText glyph="done" label={t('可用')} />
                ) : activity.status === 'review' ? (
                  <StatusText glyph="warn" label={t('待確認')} tone="wn" />
                ) : (
                  <StatusText glyph="half" label={t('解析中')} tone="pr" />
                ),
              ),
            }))}
            template="minmax(0,1fr) 90px 150px 130px 110px"
          />
        </PageBody>
      ) : (
        <div className="vbd">
          {columns.map((column) => (
            <div className="vbc" key={column.title}>
              <div className="vbch">
                {column.icon}
                <b>{column.title}</b>
                <span className="vbn">{column.rows.length}</span>
              </div>
              {/* The card list scrolls on its own so the column header stays put and the board's
                  horizontal scrollbar sits at the bottom of the viewport, not under the tallest
                  column. */}
              <div className="vbcl">
                {column.rows.length ? (
                  column.rows.map((activity) => (
                    <ActivityCard activity={activity} key={activity.id} onMenu={openCardMenu} />
                  ))
                ) : (
                  <div className="vbe">{t('沒有')}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

const DIRECTORY_COLUMNS: readonly (readonly [ActivityDirectoryStatus, string])[] = [
  ['processing', '解析中'],
  ['needs_review', '待確認'],
  ['ready', '可用'],
];

function directoryCardStatus(status: ActivityDirectoryStatus): ActivitySummary['status'] {
  if (status === 'processing') return 'parsing';
  if (status === 'needs_review') return 'review';
  return 'ready';
}

function ActivityDirectory({ surface }: { surface: 'desktop' | 'mobile' }) {
  const t = useTranslator();
  const organizationId = useActiveOrganizationId() ?? 'demo-organization';
  const classes = useDatasetStore((state) => state.classes);
  const hydrate = useDatasetStore((state) => state.hydrate);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const [search, setSearch] = useState('');
  const [classroomId, setClassroomId] = useState('');
  const [statuses, setStatuses] = useState<ActivityDirectoryStatus[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const filterCloseRef = useRef<HTMLButtonElement>(null);
  const directory = useActivityDirectory({
    organizationId,
    classroomId: classroomId || undefined,
    statuses,
    search,
  });
  const items = directory.data?.pages.flatMap((page) => page.items) ?? [];
  const denied = directory.error instanceof VispoApiError && directory.error.status === 403;
  const filtered = Boolean(search.trim() || classroomId || statuses.length);
  useEffect(() => {
    if (denied) hydrate({ activities: [] });
  }, [denied, hydrate]);
  useEffect(() => {
    if (filtersOpen) filterCloseRef.current?.focus();
  }, [filtersOpen]);
  const closeFilters = () => {
    setFiltersOpen(false);
    filterTriggerRef.current?.focus();
  };

  const filters = (
    <div className="act-f01-filters">
      <label className="act-f01-search">
        <span className="sr-only">{t('搜尋活動')}</span>
        <input
          aria-label={t('搜尋活動')}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('搜尋活動')}
          type="search"
          value={search}
        />
      </label>
      <label>
        <span className="sr-only">{t('班級')}</span>
        <select
          aria-label={t('班級')}
          onChange={(event) => setClassroomId(event.target.value)}
          value={classroomId}
        >
          <option value="">{t('全部班級')}</option>
          {classes.map((classroom) => (
            <option key={classroom.id} value={classroom.id}>
              {classroom.name}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="act-f01-statuses">
        <legend className="sr-only">{t('狀態')}</legend>
        {DIRECTORY_COLUMNS.map(([status, label]) => (
          <label key={status}>
            <input
              checked={statuses.includes(status)}
              onChange={() =>
                setStatuses((current) =>
                  current.includes(status)
                    ? current.filter((value) => value !== status)
                    : [...current, status],
                )
              }
              type="checkbox"
            />
            <span>{t(label)}</span>
          </label>
        ))}
      </fieldset>
    </div>
  );

  return (
    <section
      aria-label={t('活動庫')}
      className={`act-f01-directory act-f01-directory--${surface}`}
      data-testid={`activity-directory-${surface}`}
    >
      <header className="act-f01-heading">
        <div>
          <p>{t('教學活動')}</p>
          <h2>{t('活動庫')}</h2>
        </div>
        {surface === 'mobile' ? (
          <>
            <button
              aria-expanded={filtersOpen}
              aria-haspopup="dialog"
              className="act-f01-filter-trigger"
              onClick={() => setFiltersOpen(true)}
              ref={filterTriggerRef}
              type="button"
            >
              {t('篩選')}
            </button>
            {filtersOpen ? (
              <div
                aria-label={t('篩選活動')}
                aria-modal="true"
                className="act-f01-filter-sheet"
                onKeyDown={(event) => {
                  if (event.key === 'Escape') closeFilters();
                }}
                role="dialog"
              >
                <div className="act-f01-filter-sheet__header">
                  <strong>{t('篩選活動')}</strong>
                  <button
                    aria-label={t('關閉篩選')}
                    className="act-f01-filter-close"
                    onClick={closeFilters}
                    ref={filterCloseRef}
                    type="button"
                  >
                    {t('關閉')}
                  </button>
                </div>
                {filters}
              </div>
            ) : null}
          </>
        ) : null}
      </header>
      {surface === 'desktop' ? filters : null}
      {directory.isPending ? (
        <VispoLoading className="act-f01-state" label={t('正在載入活動')} />
      ) : directory.isError ? (
        <DirectoryState
          action={denied ? undefined : () => void directory.refetch()}
          actionLabel={t('再試一次')}
          message={denied ? t('你沒有權限查看這個活動庫') : t('暫時無法載入活動')}
        />
      ) : items.length === 0 ? (
        <DirectoryState message={filtered ? t('沒有符合條件的活動') : t('還沒有活動')} />
      ) : surface === 'mobile' ? (
        <div className="act-f01-mobile-list">
          {DIRECTORY_COLUMNS.filter(
            ([status]) => !statuses.length || statuses.includes(status),
          ).map(([status, label]) => {
            const groupItems = items.filter((item) => item.status === directoryCardStatus(status));
            return (
              <section className="act-f01-mobile-group" data-status={status} key={status}>
                <h3>
                  {t(label)} <span>{groupItems.length}</span>
                </h3>
                <div className="act-f01-mobile-group-list">
                  {groupItems.length ? (
                    groupItems.map((item) => (
                      <DirectoryCard
                        item={item}
                        key={item.id}
                        onSelect={() => pick(item.id, true)}
                      />
                    ))
                  ) : (
                    <p className="act-f01-column-empty">{t('沒有')}</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="act-f01-board">
          {DIRECTORY_COLUMNS.filter(
            ([status]) => !statuses.length || statuses.includes(status),
          ).map(([status, label]) => {
            const columnItems = items.filter((item) => item.status === directoryCardStatus(status));
            return (
              <section className="act-f01-column" key={status}>
                <h3>
                  {t(label)} <span>{columnItems.length}</span>
                </h3>
                <div className="act-f01-column-list">
                  {columnItems.length ? (
                    columnItems.map((item) => (
                      <DirectoryCard item={item} key={item.id} onSelect={() => pick(item.id)} />
                    ))
                  ) : (
                    <p className="act-f01-column-empty">{t('沒有')}</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {directory.hasNextPage ? (
        <button
          className="act-f01-load-more"
          disabled={directory.isFetchingNextPage}
          onClick={() => void directory.fetchNextPage()}
          type="button"
        >
          {directory.isFetchingNextPage ? t('正在載入活動') : t('載入更多')}
        </button>
      ) : null}
    </section>
  );
}

function DirectoryState({
  action,
  actionLabel,
  message,
}: {
  action?: () => void;
  actionLabel?: string;
  message: string;
}) {
  return (
    <div className="act-f01-state" role="status">
      <p>{message}</p>
      {action ? (
        <button onClick={action} type="button">
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function DirectoryCard({ item, onSelect }: { item: ActivitySummary; onSelect: () => void }) {
  return (
    <button className="act-f01-card" onClick={onSelect} type="button">
      <span className={`act-f01-card-status act-f01-card-status--${item.status}`}>
        {item.status === 'parsing' ? '解析中' : item.status === 'review' ? '待確認' : '可用'}
      </span>
      <strong>{item.title}</strong>
      <span>{`${item.questions} 題 · ${item.updatedLabel}`}</span>
      {item.needsReview ? <em>{`${item.needsReview} 題待確認`}</em> : null}
    </button>
  );
}

function ActivityCard({
  activity,
  onMenu,
}: {
  activity: ActivityRecord;
  onMenu(event: MouseEvent<HTMLElement>, activity: ActivityRecord): void;
}) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const display = useDisplayOptions('act');
  const pick = useWorkspaceUiStore((state) => state.pick);
  const classNames = activity.usedByClassIndexes.map((index) => classes[index].name);

  return (
    // biome-ignore lint/a11y/useSemanticElements: the row hosts its own buttons, so it cannot itself be a <button>
    <div
      className="vcard"
      onContextMenu={(event) => onMenu(event, activity)}
      role="button"
      tabIndex={0}
      {...clickable(() => pick(activity.id))}
    >
      <div className="vch">
        <span className="vcav" title={t('王維青')}>
          {t('師')}
        </span>
        <span className="vcid">{activity.id.toUpperCase()}</span>
        {activity.answerSource === 'ai' ? <span className="vcai">AI 答案</span> : null}
        <div className="sp" />
        {activity.status === 'parsing' ? (
          <span className="vcpct">{`${activity.parsedPercent ?? 0}%`}</span>
        ) : activity.status === 'review' ? (
          <span className="vcwarn">{`${activity.needsReview ?? 0} ${t('題待確認')}`}</span>
        ) : (
          <StatusIcon glyph="done" />
        )}
        <button
          className="vcog"
          title={t('更多')}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onMenu(event, activity);
          }}
        >
          <GearIcon />
        </button>
      </div>
      <h4>{activity.name}</h4>
      {activity.tags.length ? (
        <div className="vctags">
          {activity.tags.map((tag) => (
            <span className="vctag" key={tag}>
              {tag}
            </span>
          ))}
        </div>
      ) : null}
      {activity.status === 'parsing' ? (
        <span className="mtr" style={{ height: 3 }}>
          <i style={{ width: `${activity.parsedPercent ?? 0}%` }} />
        </span>
      ) : null}
      <div className="vcm">
        {display.props.includes('q') ? (
          <span className="vcmi">{`${activity.questionCount} ${t('題')}`}</span>
        ) : null}
        {display.props.includes('cls') ? (
          classNames.length ? (
            <span className="vchip">{classNames[0]}</span>
          ) : (
            <span className="vcmi">{t('未加入班級')}</span>
          )
        ) : null}
        {display.props.includes('ans') && activity.answerSource === 'ai' ? (
          <span className="vcmi">{t('AI 生成')}</span>
        ) : null}
        <div className="sp" />
        {display.props.includes('upd') ? (
          <span className="vcmi">{t(activity.updatedLabel)}</span>
        ) : null}
      </div>
    </div>
  );
}

/** The card menu and its class-picker sub-popover. */
export function useActivityMenus() {
  const t = useTranslator();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const actions = useActivityActions();

  const openCopyMenu = (event: MouseEvent<HTMLElement>, activity: ActivityRecord) => {
    const rect = event.currentTarget.getBoundingClientRect();
    window.setTimeout(
      () =>
        openPopover({
          x: Math.max(14, rect.right - 320),
          y: rect.bottom + 8,
          className: 'cmenu',
          content: (
            <CommandMenuRows
              heading={t('複製到哪個班')}
              items={classes.map((item, index) => ({
                icon: t('班'),
                title: item.name,
                description: activity.usedByClassIndexes.includes(index)
                  ? '已經在這個班'
                  : `${item.students.length} 人`,
                disabled: activity.usedByClassIndexes.includes(index),
                onSelect: () => actions.copyToClass(activity.id, index),
              }))}
            />
          ),
        }),
      10,
    );
  };

  const openCardMenu = (event: MouseEvent<HTMLElement>, activity: ActivityRecord) => {
    event.preventDefault();
    event.stopPropagation();
    const point = pointerPoint(event);
    openPopover({
      x: point.x,
      y: point.y,
      className: 'cmenu rmenu',
      content: (
        <MenuRows
          items={[
            { label: t('開啟'), shortcut: '↵', onSelect: () => pick(activity.id) },
            {
              label: t('發放'),
              onSelect: () => openModal(<AssignmentDialog activityId={activity.id} />),
            },
            { label: t('加入班級'), onSelect: () => openCopyMenu(event, activity) },
            { label: t('標籤'), onSelect: () => openModal(<TagDialog activity={activity} />) },
            {
              label: activity.archived ? t('取消封存') : t('封存'),
              onSelect: () => {
                useDatasetStore
                  .getState()
                  .updateActivity(activity.id, { archived: !activity.archived });
                notify(activity.archived ? t('已取消封存') : t('已封存'));
              },
            },
            {
              label: t('複製一份'),
              shortcut: '⌘ D',
              onSelect: () => actions.duplicate(activity.id),
            },
            'divider',
            { label: t('刪除'), danger: true, onSelect: () => actions.remove(activity.id) },
          ]}
        />
      ),
    });
  };

  return { openCardMenu, openCopyMenu };
}
